import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useLoyalty } from '../../../contexts/LoyaltyContext';
import { apiGet, apiPost } from '../../../lib/api';

// ─── Types ───────────────────────────────────────────────────────────────────

interface QuizOption {
  id: string;
  text: string;
  sortOrder: number;
}

interface QuizQuestion {
  id: string;
  text: string;
  type: 'single_choice' | 'multiple_choice';
  options: QuizOption[];
  required?: boolean;
}

interface Quiz {
  id: string;
  title: string;
  description?: string | null;
  questions: QuizQuestion[];
  completionPoints?: number | null;
  timeLimitSeconds?: number | null;
  hasResponded?: boolean;
  previousScore?: { correct: number; total: number; percent: number } | null;
}

interface RespondResult {
  pointsAwarded?: number | null;
  score?: { correct: number; total: number; percent: number } | null;
}

interface LeaderboardEntry {
  rank: number;
  firstName?: string | null;
  lastName?: string | null;
  avatarUrl?: string | null;
  score?: number;
  percent?: number;
  completedAt?: string | null;
}

interface QuizResults {
  leaderboard?: LeaderboardEntry[];
  totalResponses?: number;
}

type QuizState = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';

interface LoyaltyQuizBlockProps {
  questionnaireId?: string;
  programId?: string;
  showLeaderboard?: boolean;
}

// ─── Spinner ─────────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
    </svg>
  );
}

// ─── Timer ────────────────────────────────────────────────────────────────────

function Timer({ seconds, onExpire }: { seconds: number; onExpire: () => void }) {
  const [remaining, setRemaining] = useState(seconds);

  useEffect(() => {
    if (remaining <= 0) { onExpire(); return; }
    const t = setTimeout(() => setRemaining((r) => r - 1), 1000);
    return () => clearTimeout(t);
  }, [remaining, onExpire]);

  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;
  const urgent = remaining <= 30;

  return (
    <span className={`text-sm font-mono font-semibold ${urgent ? 'text-red-400 animate-pulse' : 'text-[var(--color-text-muted,#94a3b8)]'}`}>
      {mins}:{String(secs).padStart(2, '0')}
    </span>
  );
}

// ─── Leaderboard ─────────────────────────────────────────────────────────────

function LeaderboardView({ entries }: { entries: LeaderboardEntry[] }) {
  if (entries.length === 0) {
    return <p className="text-xs text-[var(--color-text-muted,#94a3b8)]">No leaderboard data yet.</p>;
  }

  return (
    <div className="space-y-2">
      <h4 className="text-xs font-semibold text-[var(--color-text-muted,#94a3b8)] uppercase tracking-wider">
        Top Scores
      </h4>
      {entries.slice(0, 10).map((entry) => (
        <div
          key={entry.rank}
          className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/5 border border-white/10"
        >
          <span className={`text-sm font-bold w-6 text-center ${
            entry.rank === 1 ? 'text-yellow-400' : entry.rank === 2 ? 'text-slate-300' : entry.rank === 3 ? 'text-amber-500' : 'text-[var(--color-text-muted,#94a3b8)]'
          }`}>
            {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : `#${entry.rank}`}
          </span>
          <span className="flex-1 text-sm text-[var(--color-text,#e2e8f0)] truncate">
            {entry.firstName ?? 'Anonymous'} {entry.lastName ? entry.lastName[0] + '.' : ''}
          </span>
          {entry.percent != null && (
            <span className="text-xs font-semibold text-[var(--color-primary,#a5b4fc)]">
              {entry.percent}%
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Block ────────────────────────────────────────────────────────────────────

export function LoyaltyQuizBlock({ props }: { props: LoyaltyQuizBlockProps }) {
  const { questionnaireId, showLeaderboard = false } = props;
  const { isAuthenticated } = useAuth();
  const { refetch: refetchLoyalty } = useLoyalty();

  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [results, setResults] = useState<QuizResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [quizState, setQuizState] = useState<QuizState>('NOT_STARTED');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  const [score, setScore] = useState<{ correct: number; total: number; percent: number } | null>(null);
  const [pointsAwarded, setPointsAwarded] = useState<number | null>(null);

  const startTimeRef = useRef<number | null>(null);

  const handleTimeExpire = useCallback(() => {
    // Auto-submit when timer runs out
    if (quizState === 'IN_PROGRESS') {
      submitQuiz(true);
    }
  }, [quizState]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!questionnaireId || !isAuthenticated) {
      setLoading(false);
      return;
    }
    setLoading(true);
    apiGet<Quiz>(`/customer/questionnaires/${questionnaireId}`)
      .then(async (data) => {
        setQuiz(data);
        if (data.hasResponded) {
          setQuizState('COMPLETED');
          if (data.previousScore) setScore(data.previousScore);
          if (showLeaderboard) {
            try {
              const res = await apiGet<QuizResults>(`/customer/questionnaires/${questionnaireId}/results`);
              setResults(res);
            } catch { /* ignore */ }
          }
        }
      })
      .catch((err) => {
        setError((err as Error).message ?? 'Failed to load quiz');
      })
      .finally(() => setLoading(false));
  }, [questionnaireId, isAuthenticated, showLeaderboard]);

  function startQuiz() {
    startTimeRef.current = Date.now();
    setQuizState('IN_PROGRESS');
    setCurrentIndex(0);
    setAnswers({});
  }

  function toggleAnswer(questionId: string, optionId: string, multi: boolean) {
    setAnswers((prev) => {
      const current = prev[questionId] ?? [];
      if (multi) {
        return {
          ...prev,
          [questionId]: current.includes(optionId)
            ? current.filter((id) => id !== optionId)
            : [...current, optionId],
        };
      }
      return { ...prev, [questionId]: [optionId] };
    });
  }

  async function submitQuiz(timedOut = false) {
    if (!quiz) return;
    setSubmitting(true);
    setError(null);

    const timeTakenSeconds = startTimeRef.current
      ? Math.round((Date.now() - startTimeRef.current) / 1000)
      : undefined;

    try {
      const payload = {
        answers: quiz.questions.map((q) => ({
          questionId: q.id,
          optionIds: answers[q.id] ?? [],
        })),
        timeTakenSeconds,
        timedOut,
      };

      const result = await apiPost<RespondResult>(
        `/customer/questionnaires/${questionnaireId}/respond`,
        payload,
      );

      setPointsAwarded(result.pointsAwarded ?? null);
      if (result.score) setScore(result.score);
      setQuizState('COMPLETED');
      refetchLoyalty();

      if (showLeaderboard) {
        try {
          const res = await apiGet<QuizResults>(`/customer/questionnaires/${questionnaireId}/results`);
          setResults(res);
        } catch { /* ignore */ }
      }
    } catch (err) {
      const errData = (err as Error & { data?: { error?: string } }).data;
      setError(errData?.error ?? (err instanceof Error ? err.message : 'Failed to submit quiz'));
    } finally {
      setSubmitting(false);
    }
  }

  // ── Not authenticated ──────────────────────────────────────────────────────

  if (!isAuthenticated) {
    return (
      <div className="loyalty-card p-6 text-center">
        <span className="text-3xl mb-3 block">🎯</span>
        <p className="text-sm text-[var(--color-text-muted,#94a3b8)] mb-3">
          Log in to take this quiz and earn points.
        </p>
        <a
          href="/login"
          className="inline-block px-5 py-2 rounded-lg bg-[var(--color-primary,#6366f1)] text-white text-sm font-semibold hover:opacity-90 transition-opacity"
        >
          Log in to play
        </a>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loyalty-card p-8 flex justify-center">
        <svg className="w-5 h-5 animate-spin text-[var(--color-primary,#6366f1)]" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
        </svg>
      </div>
    );
  }

  if (error && !quiz) {
    return (
      <div className="loyalty-card p-4">
        <p className="text-sm text-red-400">{error}</p>
      </div>
    );
  }

  if (!quiz) return null;

  // ── NOT_STARTED ────────────────────────────────────────────────────────────

  if (quizState === 'NOT_STARTED') {
    return (
      <div className="loyalty-card p-6 space-y-5">
        <div className="text-center">
          <span className="text-4xl block mb-3">🎯</span>
          <h3 className="loyalty-heading text-base font-bold text-[var(--color-text,#e2e8f0)] mb-1">
            {quiz.title}
          </h3>
          {quiz.description && (
            <p className="text-sm text-[var(--color-text-muted,#94a3b8)]">{quiz.description}</p>
          )}
        </div>

        <div className="flex flex-wrap justify-center gap-3 text-xs">
          <div className="flex items-center gap-1.5 text-[var(--color-text-muted,#94a3b8)]">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {quiz.questions.length} question{quiz.questions.length !== 1 ? 's' : ''}
          </div>
          {quiz.completionPoints != null && quiz.completionPoints > 0 && (
            <div className="flex items-center gap-1.5 text-yellow-400">
              <span>⭐</span>
              +{quiz.completionPoints} pts
            </div>
          )}
          {quiz.timeLimitSeconds != null && (
            <div className="flex items-center gap-1.5 text-[var(--color-text-muted,#94a3b8)]">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {Math.ceil(quiz.timeLimitSeconds / 60)} min
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={startQuiz}
          className="w-full py-3 rounded-xl bg-[var(--color-primary,#6366f1)] text-white text-sm font-bold hover:opacity-90 transition-opacity"
        >
          Start Quiz
        </button>
      </div>
    );
  }

  // ── IN_PROGRESS ────────────────────────────────────────────────────────────

  if (quizState === 'IN_PROGRESS') {
    const total = quiz.questions.length;
    const q = quiz.questions[currentIndex];
    const isMulti = q.type === 'multiple_choice';
    const selected = answers[q.id] ?? [];
    const progressPercent = Math.round(((currentIndex) / total) * 100);
    const isLast = currentIndex === total - 1;

    return (
      <div className="loyalty-card p-6 space-y-4">
        {/* Header: progress + timer */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center justify-between text-xs text-[var(--color-text-muted,#94a3b8)] mb-1">
              <span>Question {currentIndex + 1} of {total}</span>
              <span>{progressPercent}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div
                className="h-full rounded-full bg-[var(--color-primary,#6366f1)] transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
          {quiz.timeLimitSeconds != null && (
            <Timer seconds={quiz.timeLimitSeconds} onExpire={handleTimeExpire} />
          )}
        </div>

        {/* Question */}
        <p className="text-sm font-semibold text-[var(--color-text,#e2e8f0)]">{q.text}</p>

        {/* Options */}
        <div className="space-y-2">
          {q.options.map((opt) => {
            const checked = selected.includes(opt.id);
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => toggleAnswer(q.id, opt.id, isMulti)}
                className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm transition-all duration-150 ${
                  checked
                    ? 'border-[var(--color-primary,#6366f1)] bg-[var(--color-primary,#6366f1)]/20 font-medium text-[var(--color-primary,#a5b4fc)]'
                    : 'border-white/10 text-[var(--color-text-muted,#94a3b8)] hover:border-[var(--color-primary,#6366f1)]/50 hover:text-[var(--color-text,#e2e8f0)]'
                }`}
              >
                {opt.text}
              </button>
            );
          })}
        </div>

        {error && (
          <p className="text-xs text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg px-3 py-2">
            {error}
          </p>
        )}

        {/* Navigation */}
        <div className="flex gap-2 pt-1">
          {currentIndex > 0 && (
            <button
              type="button"
              onClick={() => setCurrentIndex((i) => i - 1)}
              className="px-4 py-2 rounded-lg border border-white/10 text-sm text-[var(--color-text-muted,#94a3b8)] hover:text-[var(--color-text,#e2e8f0)] hover:border-white/20 transition-colors"
            >
              Back
            </button>
          )}
          {!isLast ? (
            <button
              type="button"
              onClick={() => setCurrentIndex((i) => i + 1)}
              className="flex-1 py-2 rounded-lg bg-[var(--color-primary,#6366f1)] text-white text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={() => submitQuiz()}
              disabled={submitting}
              className="flex-1 py-2 rounded-lg bg-[var(--color-primary,#6366f1)] text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting && <Spinner />}
              Submit Quiz
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── COMPLETED ──────────────────────────────────────────────────────────────

  const scorePercent = score?.percent ?? quiz.previousScore?.percent;
  const correctCount = score?.correct ?? quiz.previousScore?.correct;
  const totalCount = score?.total ?? quiz.questions.length;

  return (
    <div className="loyalty-card p-6 space-y-5">
      {/* Result header */}
      <div className="text-center space-y-2">
        <span className="text-4xl block">
          {scorePercent != null && scorePercent >= 80 ? '🏆' : scorePercent != null && scorePercent >= 50 ? '🎯' : '💪'}
        </span>
        <h3 className="loyalty-heading text-base font-bold text-[var(--color-text,#e2e8f0)]">
          {quiz.hasResponded && pointsAwarded === null ? 'Already completed!' : 'Quiz Complete!'}
        </h3>

        {score || quiz.previousScore ? (
          <div className="flex flex-col items-center gap-1">
            {scorePercent != null && (
              <span className="text-3xl font-bold text-[var(--color-primary,#a5b4fc)]">
                {scorePercent}%
              </span>
            )}
            {correctCount != null && (
              <span className="text-sm text-[var(--color-text-muted,#94a3b8)]">
                {correctCount} / {totalCount} correct
              </span>
            )}
          </div>
        ) : null}

        {pointsAwarded != null && pointsAwarded > 0 && (
          <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-yellow-400 bg-yellow-400/10 border border-yellow-400/20 rounded-full px-4 py-1.5">
            ⭐ +{pointsAwarded} points earned!
          </div>
        )}
      </div>

      {error && (
        <p className="text-xs text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg px-3 py-2">
          {error}
        </p>
      )}

      {/* Leaderboard */}
      {showLeaderboard && results?.leaderboard && (
        <LeaderboardView entries={results.leaderboard} />
      )}
    </div>
  );
}
